import { useDebouncedState } from '@mantine/hooks';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { LuEllipsis, LuLoader, LuPin, LuPlus } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { PersonOrgType } from '@ndvr/auth-api';
import { NoteView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDockState } from '@/views/dock/dock-state';
import { formatDateTime, useRequiredParams } from 'utils';
import { useHouseholdTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import { CustomTablePage, PersonOrgLogoCard, SearchInput } from 'widgets';

import { useAdvisorLogin } from '../household-queries';
import { DeleteHouseholdNoteDialog } from './DeleteHouseholdNoteDialog';
import { useCreatePortfolioFromTranscript, useNotesQuery, usePinNoteMutation } from './note-queries';
import { filterNotes, getPinnedNoteGUIDs, MEETING_ASSISTANT_GUID } from './note-utils';

const columnHelper = createColumnHelper<NoteView>();

export const HouseholdNotesTab = () => {
    const { addCreateNoteDockItem } = useDockState();
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const navigate = useNavigate();
    const notesViewQuery = useNotesQuery(globalAccountGUID);
    const { nameFor, isPending: isAuthorNamePending } = useHouseholdTeamNameResolver(globalAccountGUID);

    const [searchTerm, setSearchTerm] = useDebouncedState('', 500);

    const notes = useMemo(() => {
        if (!notesViewQuery.data) {
            return [];
        }
        return filterNotes(notesViewQuery.data, { searchTerm });
    }, [notesViewQuery.data, searchTerm]);

    const rowPinning = useMemo(() => ({ top: getPinnedNoteGUIDs(notes) }), [notes]);

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'pinned',
                header: '',
                cell: ({ row }) =>
                    row.original.pinnedAt ? (
                        <LuPin className="text-ndvr-dark h-4 w-4" aria-label="Pinned to top" />
                    ) : null,
                size: 32,
                minSize: 32,
                meta: { isDisplayCol: true },
            }),
            columnHelper.accessor('subject', {
                header: 'Subject',
                cell: ({ row }) => <div className="max-w-48 truncate">{row.original.subject}</div>,
            }),
            columnHelper.accessor(
                (row) => (row.createdBy === MEETING_ASSISTANT_GUID ? 'Claire' : nameFor(row.createdBy) || ''),
                {
                    id: 'author',
                    header: 'Author',
                    cell: ({ row }) => {
                        const note = row.original;
                        if (note.createdBy === MEETING_ASSISTANT_GUID) {
                            return <PersonOrgLogoCard name="Claire™" personOrgType={PersonOrgType.PERSON} to="" />;
                        }
                        if (isAuthorNamePending) {
                            return null;
                        }
                        const authorName = nameFor(note.createdBy);
                        if (authorName) {
                            return <PersonOrgLogoCard name={authorName} personOrgType={PersonOrgType.PERSON} to="" />;
                        }
                        return `Unknown Advisor - ${note.createdBy}`;
                    },
                },
            ),
            columnHelper.accessor('createdAt', {
                header: 'Created On',
                cell: ({ row }) => formatDateTime(row.original.createdAt),
            }),
            columnHelper.accessor((row) => row.updatedAt ?? row.createdAt, {
                id: 'updatedAt',
                header: 'Last Modified',
                cell: ({ row }) => formatDateTime(row.original.updatedAt ?? row.original.createdAt),
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <NoteActions note={row.original} globalAccountGUID={globalAccountGUID} />
                    </div>
                ),
                size: 48,
                minSize: 48,
                meta: { isDisplayCol: true },
            }),
        ],
        [nameFor, isAuthorNamePending, globalAccountGUID],
    );

    return (
        <div>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <SearchInput round placeholder="Search notes..." onChange={(e) => setSearchTerm(e.target.value)} />
                    <Button variant="outline2" size="sm" onClick={() => addCreateNoteDockItem(globalAccountGUID)}>
                        <LuPlus className="h-4 w-4" />
                        <span>Note</span>
                    </Button>
                </div>
            </HouseholdHeaderActionsPortal>
            <CustomTablePage
                data={notes}
                columns={columns}
                isLoading={notesViewQuery.isLoading}
                rowOnClick={(note) => navigate(`../notes/view?noteGUID=${note.noteGUID}`)}
                getRowId={(note) => note.noteGUID}
                rowPinning={rowPinning}
                exportName="notes"
                cardClassName="m-0 border-0 shadow-none"
                defaultSortCol="createdAt"
                defaultSortDirection="desc"
            />
        </div>
    );
};

interface NoteActionsProps {
    note: NoteView;
    globalAccountGUID: string;
}

const NoteActions = ({ note, globalAccountGUID }: NoteActionsProps) => {
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const createPortfolioMutation = useCreatePortfolioFromTranscript(note.householdGUID, note.noteGUID);
    const advisorLoginMutation = useAdvisorLogin();
    const pinNoteMutation = usePinNoteMutation(globalAccountGUID);

    const handleConvertToFinancialPlan = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        if (note.meetingBotGUID) {
            createPortfolioMutation.mutate(note.meetingBotGUID);
        }
    };

    // Close dropdown when mutation completes (success or error)
    React.useEffect(() => {
        if (!createPortfolioMutation.isPending && createPortfolioMutation.isIdle === false) {
            setDropdownOpen(false);
        }
    }, [createPortfolioMutation.isPending, createPortfolioMutation.isIdle]);

    return (
        <div>
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                    <div className="hover:bg-ndvr-seafoam/50 hover:text-ndvr-dark inline-flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium">
                        <LuEllipsis className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        onClick={() => {
                            navigate(`../notes/view?noteGUID=${note.noteGUID}`);
                        }}
                    >
                        View
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            navigate(`../notes/edit?noteGUID=${note.noteGUID}`);
                        }}
                    >
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => {
                            pinNoteMutation.mutate({ noteGUID: note.noteGUID, pinned: !note.pinnedAt });
                        }}
                        disabled={pinNoteMutation.isPending}
                    >
                        {note.pinnedAt ? 'Unpin' : 'Pin to top'}
                    </DropdownMenuItem>
                    {note.meetingBotGUID && !note.accountGroupGUID && (
                        <DropdownMenuItem
                            onSelect={handleConvertToFinancialPlan}
                            disabled={createPortfolioMutation.isPending}
                        >
                            {createPortfolioMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Convert to Financial Plan
                        </DropdownMenuItem>
                    )}
                    {note.accountGroupGUID && (
                        <DropdownMenuItem
                            onClick={() =>
                                advisorLoginMutation.mutate({
                                    householdGUID: globalAccountGUID,
                                    redirect: `/lab/${note.accountGroupGUID}`,
                                })
                            }
                            disabled={advisorLoginMutation.isPending}
                        >
                            Analyze in Portfolio Lab&#8482;
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => {
                            setDialogOpen(true);
                        }}
                    >
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {<DeleteHouseholdNoteDialog open={dialogOpen} onOpenChange={setDialogOpen} note={note} />}
        </div>
    );
};
